import { CreditCard, Calendar, AlertTriangle, CheckCircle2, Settings, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CreditAccount } from "@/hooks/useCredit";

interface AutopayStatusCardProps {
  credit: CreditAccount;
  sourceAccountName: string | null;
  onSetup: () => void;
  onDisable: () => void;
}

const AutopayStatusCard = ({ credit, sourceAccountName, onSetup, onDisable }: AutopayStatusCardProps) => {
  const enabled = credit.autopay_enabled;
  const status = credit.autopay_status;
  const isFailed = status === "failed";

  const amountLabel = (() => {
    switch (credit.autopay_amount_type) {
      case "minimum": return "Minimum Payment";
      case "full": return "Full Balance";
      case "statement": return "Statement Balance";
      case "custom": return `Custom — $${credit.autopay_custom_amount?.toLocaleString() || 0}`;
      default: return "—";
    }
  })();

  const nextDate = credit.autopay_next_run_date
    ? new Date(credit.autopay_next_run_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  const lastDate = credit.autopay_last_run_date
    ? new Date(credit.autopay_last_run_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  if (!enabled) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Autopay is off</span>
          </div>
          <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={onSetup}>
            <Settings className="w-3 h-3" />
            Set Up Autopay
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          Enable autopay to automatically pay your credit card on each due date.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 transition-all ${
      isFailed
        ? "border-destructive/50 bg-destructive/5"
        : "border-emerald-500/30 bg-emerald-500/5"
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isFailed ? (
            <AlertTriangle className="w-4 h-4 text-destructive" />
          ) : (
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          )}
          <span className={`text-sm font-semibold ${isFailed ? "text-destructive" : "text-emerald-600"}`}>
            Autopay {isFailed ? "Failed" : "Active"}
          </span>
        </div>
        <div className="flex gap-1.5">
          <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={onSetup}>
            <Settings className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-destructive hover:text-destructive" onClick={onDisable}>
            <Power className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {isFailed && (
        <p className="text-xs text-destructive mb-3 bg-destructive/10 rounded-lg px-3 py-2">
          ⚠️ Last autopay failed due to insufficient funds. Please add funds or update your payment source.
        </p>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Source Account</p>
          <p className="font-medium text-foreground">{sourceAccountName || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Payment Type</p>
          <p className="font-medium text-foreground">{amountLabel}</p>
        </div>
        {nextDate && (
          <div>
            <p className="text-xs text-muted-foreground">Next Payment</p>
            <p className="font-medium text-foreground flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {nextDate}
            </p>
          </div>
        )}
        {lastDate && (
          <div>
            <p className="text-xs text-muted-foreground">Last Payment</p>
            <p className="font-medium text-foreground">
              {lastDate} — ${credit.autopay_last_amount?.toLocaleString() || 0}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutopayStatusCard;
